import { useState, useEffect } from "react";

export const C = {
  rose: "#8B2252",
  roseMid: "#993356",
  rosePale: "#FBEAF0",
  roseBorder: "#F4C0D1",
  green: "#3B6D11",
  greenBg: "#EAF3DE",
  greenBorder: "#C0DD97",
  red: "#A32D2D",
  redBg: "#FCEBEB",
  teal: "#0F6E56",
  tealBg: "#E1F5EE",
  amber: "#633806",
  amberBg: "#FAEEDA",
  gray: "#888780",
  grayBg: "#F1EFE8",
  text: "#1a1a1a",
  textMain: "#1a1a1a",
  textSec: "#6b6860",
  border: "rgba(0,0,0,0.1)",
  bg: "#faf9f7",
  surface: "#ffffff",
};

// Hook para responsividade
export const useResponsive = () => {
  const [screen, setScreen] = useState({
    isMobile: typeof window !== 'undefined' ? window.innerWidth <= 768 : false,
    isTablet: typeof window !== 'undefined' ? window.innerWidth <= 1024 && window.innerWidth > 768 : false,
    isDesktop: typeof window !== 'undefined' ? window.innerWidth > 1024 : true,
  });

  useEffect(() => {
    const handleResize = () => {
      setScreen({
        isMobile: window.innerWidth <= 768,
        isTablet: window.innerWidth <= 1024 && window.innerWidth > 768,
        isDesktop: window.innerWidth > 1024,
      });
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return screen;
};

export const FONTS = `@import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,700;1,400;1,700&family=DM+Sans:wght@300;400;500&display=swap');`;

export const globalCSS = `
  ${FONTS}
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  html { scroll-behavior: smooth; }
  body { font-family: 'DM Sans', sans-serif; background: ${C.bg}; color: ${C.text}; }
  a { color: inherit; text-decoration: none; }
  button { cursor: pointer; font-family: 'DM Sans', sans-serif; }
  input, textarea, select { font-family: 'DM Sans', sans-serif; outline: none; }
  @keyframes fadeUp {
    from { opacity: 0; transform: translateY(20px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
  .anim-1 { animation: fadeUp 0.5s 0s ease both; }
  .anim-2 { animation: fadeUp 0.5s 0.1s ease both; }
  .anim-3 { animation: fadeUp 0.5s 0.2s ease both; }
  .anim-4 { animation: fadeUp 0.5s 0.3s ease both; }
  .anim-5 { animation: fadeUp 0.5s 0.4s ease both; }
`;

// ── Stars ──────────────────────────────────────────────────────────────────────
export const Stars = ({ value = 4, max = 5, size = 15, interactive = false, onChange }) => {
  const [hov, setHov] = useState(0);
  return (
    <span style={{ display: "inline-flex", gap: 2 }}>
      {Array.from({ length: max }).map((_, i) => (
        <span key={i}
          onClick={() => interactive && onChange?.(i + 1)}
          onMouseEnter={() => interactive && setHov(i + 1)}
          onMouseLeave={() => interactive && setHov(0)}
          style={{ fontSize: size, color: (hov || value) > i ? C.roseMid : "#F4C0D1", cursor: interactive ? "pointer" : "default", transition: "color 0.1s" }}
        >★</span>
      ))}
    </span>
  );
};

// ── Avatar ─────────────────────────────────────────────────────────────────────
export const Avatar = ({ initials, bg = C.rosePale, color = C.roseMid, size = 36, radius = "50%" }) => (
  <div style={{ width: size, height: size, borderRadius: radius, background: bg, color, display: "flex", alignItems: "center", justifyContent: "center", fontSize: size * 0.35, fontWeight: 500, flexShrink: 0 }}>
    {initials}
  </div>
);

// ── Chip ───────────────────────────────────────────────────────────────────────
export const Chip = ({ label, variant = "neutral" }) => {
  const s = { good: { bg: C.greenBg, color: C.green, border: C.greenBorder }, bad: { bg: C.redBg, color: C.red, border: "#F7C1C1" }, neutral: { bg: C.grayBg, color: C.gray, border: "#d3d1c7" }, rose: { bg: C.rosePale, color: C.roseMid, border: C.roseBorder } }[variant] || {};
  return <span style={{ background: s.bg, color: s.color, border: `0.5px solid ${s.border}`, borderRadius: 100, padding: "3px 12px", fontSize: 11, fontWeight: 500 }}>{label}</span>;
};

// ── Badge ──────────────────────────────────────────────────────────────────────
export const Badge = ({ label, variant = "live" }) => {
  const s = { live: { bg: C.greenBg, color: C.green }, draft: { bg: C.grayBg, color: C.gray }, pro: { bg: C.rosePale, color: C.roseMid }, blue: { bg: "#E6F1FB", color: "#185FA5" } }[variant] || {};
  return <span style={{ background: s.bg, color: s.color, borderRadius: 100, padding: "3px 12px", fontSize: 11, fontWeight: 500 }}>{label}</span>;
};

// ── Button ─────────────────────────────────────────────────────────────────────
export const Btn = ({ children, variant = "primary", onClick, style: sx = {}, full = false, disabled = false }) => {
  const base = { border: "none", borderRadius: 100, padding: "12px 26px", fontSize: 14, fontWeight: 500, transition: "opacity 0.15s, transform 0.1s", width: full ? "100%" : undefined, opacity: disabled ? 0.5 : 1, ...sx };
  const v = {
    primary:   { background: C.roseMid, color: "#fff" },
    secondary: { background: "transparent", color: C.roseMid, border: `1.5px solid ${C.roseBorder}` },
    ghost:     { background: "transparent", color: C.roseMid },
    white:     { background: "#fff", color: C.roseMid },
    dark:      { background: "#1a1a1a", color: "#fff" },
    danger:    { background: C.redBg, color: C.red, border: `1px solid #F7C1C1` },
  }[variant] || {};
  return (
    <button disabled={disabled} onClick={onClick} style={{ ...base, ...v }}
      onMouseEnter={e => !disabled && (e.currentTarget.style.opacity = "0.82")}
      onMouseLeave={e => !disabled && (e.currentTarget.style.opacity = "1")}
    >{children}</button>
  );
};

// ── Input ──────────────────────────────────────────────────────────────────────
export const Input = ({ label, name, type = "text", value, onChange, placeholder, required }) => {
  const [showPassword, setShowPassword] = useState(false);
  const isPassword = type === "password";
  const inputType = isPassword ? (showPassword ? "text" : "password") : type;
  
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      {label && <label style={{ fontSize: 13, fontWeight: 500, color: C.textSec }}>{label}{required && <span style={{ color: C.roseMid }}> *</span>}</label>}
      <div style={{ position: "relative" }}>
        <input 
          name={name} 
          type={inputType} 
          value={value} 
          onChange={onChange} 
          placeholder={placeholder} 
          style={{ 
            border: `0.5px solid ${C.border}`, 
            borderRadius: 10, 
            padding: isPassword ? "10px 44px 10px 14px" : "10px 14px", 
            fontSize: 14, 
            color: C.text, 
            background: C.surface, 
            transition: "border-color 0.15s",
            width: "100%"
          }}
          onFocus={e => e.target.style.borderColor = C.roseBorder}
          onBlur={e => e.target.style.borderColor = C.border}
        />
        {isPassword && (
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            style={{
              position: "absolute",
              right: 10,
              top: "50%",
              transform: "translateY(-50%)",
              background: "none",
              border: "none",
              padding: 4,
              cursor: "pointer",
              color: C.textSec,
              fontSize: 16,
              display: "flex",
              alignItems: "center",
              justifyContent: "center"
            }}
            aria-label={showPassword ? "Esconder senha" : "Mostrar senha"}
          >
            {showPassword ? (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
                <line x1="1" y1="1" x2="23" y2="23"/>
              </svg>
            ) : (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                <circle cx="12" cy="12" r="3"/>
              </svg>
            )}
          </button>
        )}
      </div>
    </div>
  );
};

// ── Card ───────────────────────────────────────────────────────────────────────
export const Card = ({ children, style: sx = {}, hover = false }) => {
  const [h, setH] = useState(false);
  return (
    <div onMouseEnter={() => hover && setH(true)} onMouseLeave={() => hover && setH(false)}
      style={{ background: C.surface, border: `0.5px solid ${C.border}`, borderRadius: 16, padding: "20px 24px", transition: "box-shadow 0.2s, transform 0.2s", boxShadow: h ? "0 8px 32px rgba(153,51,86,0.1)" : "none", transform: h ? "translateY(-2px)" : "none", ...sx }}>
      {children}
    </div>
  );
};

// ── Section title ──────────────────────────────────────────────────────────────
export const SectionTitle = ({ tag, title, sub, center = false }) => (
  <div style={{ textAlign: center ? "center" : "left", marginBottom: 36 }}>
    {tag && <div style={{ fontSize: 11, fontWeight: 500, letterSpacing: "1.5px", textTransform: "uppercase", color: C.roseMid, marginBottom: 8 }}>{tag}</div>}
    <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: 30, fontWeight: 700, lineHeight: 1.2, marginBottom: 8 }}>{title}</h2>
    {sub && <p style={{ fontSize: 15, color: C.textSec, lineHeight: 1.7 }}>{sub}</p>}
  </div>
);

// ── Metric card ────────────────────────────────────────────────────────────────
export const MetricCard = ({ value, label, delta, deltaPos = true }) => (
  <Card style={{ padding: "18px 20px" }}>
    <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 30, fontWeight: 700, color: C.roseMid }}>{value}</div>
    <div style={{ fontSize: 12, color: C.textSec, marginTop: 4 }}>{label}</div>
    {delta && <div style={{ marginTop: 8, display: "inline-block", background: deltaPos ? C.greenBg : C.redBg, color: deltaPos ? C.green : C.red, borderRadius: 100, padding: "2px 10px", fontSize: 11 }}>{delta}</div>}
  </Card>
);
